import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Briefcase,
  Award,
  CalendarDays,
  Star,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  UserPlus,
  MessageSquare,
  Pencil,
  XCircle,
  Clock,
  CheckCircle,
  PlayCircle,
  FileText,
  Loader2,
  Trash2,
  Pin,
  Eye,
  Download,
  Video,
  Upload,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Calendar as CalendarPrimitive } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { useAbility } from "@casl/react";
import { useAuth } from "../../context/AuthContext";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { api } from "../../utils/api";
import { toast } from "sonner";
import { formatSydneyDateLong, formatSydneyDateShort, formatTimestamp } from "../../utils/helpers";

interface Candidate {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  address: string | null;
  currentPosition: string | null;
  currentCompany: string | null;
  source: string | null;
  yearsExperience: number | null;
  linkedInProfile: string | null;
  status: string;
  rejectionReason: string | null;
  overallRating: number | null;
  assessmentNotes: string | null;
  createdAt: string;
  requisition: {
    id: number;
    jobRole: { id: number; roleName: string } | null;
    requisitionViewers?: { id: number; user: { id: number; name: string } }[];
  } | null;
  jobPosition: { id: number; positionCode: string } | null;
}

interface Note {
  id: number;
  content: string;
  isPinned: boolean;
  createdAt: string;
  createdBy: { id: number; name: string } | null;
}

interface Skill {
  id: number;
  skillName: string;
  proficiencyLevel: "Beginner" | "Intermediate" | "Advanced" | "Expert" | null;
  createdAt: string;
}

interface Experience {
  id: number;
  roleTitle: string;
  companyName: string | null;
  startDate: string | null;
  endDate: string | null;
  responsibilities: string | null;
  createdAt: string;
}

interface Interview {
  id: number;
  interviewType: string;
  scheduledAt: string;
  durationMinutes: number;
  location: string | null;
  meetingLink: string | null;
  status: string;
  feedback: string | null;
  rating: number | null;
  outcome: string;
  recommendation: string | null;
  createdAt: string;
}

interface Attachment {
  id: number;
  filename: string;
  fileUrl: string;
  fileType: string | null;
  fileSize: number;
  createdAt: string;
  createdBy: { id: number; name: string } | null;
}

interface Offer {
  id: number;
  status: "DRAFT" | "SENT" | "ACCEPTED" | "REJECTED" | "EXPIRED";
  salaryOffered: string | number | null;
  startDate: string | null;
  expiryDate: string | null;
  sentAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  supersededAt: string | null;
  supersededBy: { id: number; name: string } | null;
  offerLetterContent: string | null;
  offerTemplate: { id: number; name: string } | null;
  requisitionOrganization: { id: number; companyName: string } | null;
  createdBy: { id: number; name: string } | null;
  createdAt: string;
}

const formatCandidateStatus = (status: string) => {
  switch (status) {
    case "SHORTLISTED": return "Shortlisted";
    case "PHONE_SCREEN": return "Phone Screen";
    case "INTERVIEW": return "Interview";
    case "PRE_EMPLOYMENT_COMMENCED": return "Pre-Employment Commenced";
    case "PRE_EMPLOYMENT_COMPLETED": return "Pre-Employment Completed";
    case "DRAFT_OFFER": return "Draft Offer";
    case "OFFER_SENT": return "Offer Sent";
    case "OFFER_ACCEPTED": return "Offer Accepted";
    case "OFFER_REJECTED": return "Offer Rejected";
    case "OFFER_EXPIRED": return "Offer Expired";
    case "REJECTED": return "Rejected";
    case "WITHDREW": return "Withdrew";
    case "HIRING_COMMENCED": return "Hiring Commenced";
    case "HIRED": return "Hired";
    default: return status.replace(/_/g, " ");
  }
};

const getStatusBadgeStyle = (status: string) => {
  switch (status) {
    case "SHORTLISTED": return "bg-blue-50 text-blue-700 border-blue-100";
    case "PHONE_SCREEN": return "bg-indigo-50 text-indigo-700 border-indigo-100";
    case "INTERVIEW": return "bg-purple-50 text-purple-700 border-purple-100";
    case "PRE_EMPLOYMENT_COMMENCED": return "bg-teal-50 text-teal-700 border-teal-100";
    case "PRE_EMPLOYMENT_COMPLETED": return "bg-emerald-50 text-emerald-700 border-emerald-100";
    case "DRAFT_OFFER":
    case "OFFER_SENT": return "bg-orange-50 text-orange-700 border-orange-100";
    case "OFFER_ACCEPTED":
    case "HIRING_COMMENCED":
    case "HIRED": return "bg-green-50 text-green-700 border-green-100";
    case "REJECTED":
    case "OFFER_REJECTED":
    case "OFFER_EXPIRED":
    case "WITHDREW": return "bg-gray-100 text-gray-600 border-gray-100";
    default: return "bg-gray-50 text-gray-600 border-gray-100";
  }
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  return formatSydneyDateLong(dateStr) || "—";
};

const formatShortDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  return formatSydneyDateShort(dateStr) || "—";
};

const formatDateTime = (dateStr: string | null) => {
  if (!dateStr) return "—";
  return formatTimestamp(dateStr) || "—";
};

const getInitials = (firstName: string, lastName: string) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

const getAllowedNextStatuses = (status: string | undefined): string[] => {
  switch (status) {
    case "SHORTLISTED": return ["PHONE_SCREEN"];
    case "PHONE_SCREEN": return ["INTERVIEW", "REJECTED", "PRE_EMPLOYMENT_COMMENCED"];
    case "INTERVIEW": return ["PRE_EMPLOYMENT_COMMENCED"];
    case "PRE_EMPLOYMENT_COMMENCED": return ["PRE_EMPLOYMENT_COMPLETED"];
    case "PRE_EMPLOYMENT_COMPLETED": return [];
    case "DRAFT_OFFER": return ["OFFER_SENT", "REJECTED"];
    case "OFFER_SENT": return ["OFFER_ACCEPTED", "OFFER_REJECTED"];
    case "OFFER_ACCEPTED": return ["HIRING_COMMENCED"];
    case "HIRING_COMMENCED": return ["HIRED"];
    default: return [];
  }
};

const getStatusDescription = (status: string) => {
  switch (status) {
    case "SHORTLISTED": return "Candidate has been shortlisted for review";
    case "PHONE_SCREEN": return "Phone screening in progress";
    case "INTERVIEW": return "Candidate is in interview process";
    case "PRE_EMPLOYMENT_COMMENCED": return "Pre-employment checks in progress";
    case "PRE_EMPLOYMENT_COMPLETED": return "Pre-employment checks completed";
    case "DRAFT_OFFER": return "Drafting employment offer";
    case "OFFER_SENT": return "Offer has been sent to candidate";
    case "OFFER_ACCEPTED": return "Candidate has accepted the offer";
    case "OFFER_REJECTED": return "Candidate rejected the offer";
    case "REJECTED": return "Candidate has been rejected";
    case "WITHDREW": return "Candidate withdrew from process";
    case "HIRING_COMMENCED": return "Hiring commenced — onboarding in progress";
    case "HIRED": return "Candidate hired and onboarded";
    default: return "";
  }
};

const CandidateDetail: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { id } = useParams();

  const canReadCandidate = ability.can(ACTIONS.READ, RESOURCES.CANDIDATE_DETAIL) || ability.can(ACTIONS.READ, RESOURCES.CANDIDATE_LIST) || ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_MODULE);

  const { currentUser } = useAuth();

  const canManageCandidate = ability.can(ACTIONS.CREATE, RESOURCES.CANDIDATE_DETAIL);
  const canScheduleInterview = ability.can(ACTIONS.CREATE, RESOURCES.CANDIDATE_INTERVIEW_SCHEDULE);
  const canUpdateInterview = ability.can(ACTIONS.UPDATE, RESOURCES.CANDIDATE_INTERVIEW_SCHEDULE);
  // Completing an interview is gated separately on the backend
  // (update:interview_status on PATCH /api/interviews/:id/complete)
  const canCompleteInterview = ability.can(ACTIONS.UPDATE, RESOURCES.INTERVIEW_STATUS);
  const canDeleteInterview = ability.can(ACTIONS.DELETE, RESOURCES.CANDIDATE_INTERVIEW_SCHEDULE);
  const canManageReject = ability.can(ACTIONS.MANAGE, RESOURCES.CANDIDATE_MANAGE_REJECT);
  const canManageStatus = ability.can(ACTIONS.MANAGE, RESOURCES.CANDIDATE_MANAGE_STATUS_MANAGEMENT);
  // Fallback status-change path (PATCH /api/candidates/:id/status) for users
  // without the manage permission
  const canPatchStatus = ability.can(ACTIONS.UPDATE, RESOURCES.CANDIDATE_STATUS);
  const canManageExtendOffer = ability.can(ACTIONS.MANAGE, RESOURCES.CANDIDATE_MANAGE_EXTEND_OFFER) || ability.can(ACTIONS.UPDATE, RESOURCES.CANDIDATE_OFFER);
  const canReadNotes = ability.can(ACTIONS.READ, RESOURCES.CANDIDATE_NOTES_DETAIL);
  const canReadAttachments = ability.can(ACTIONS.READ, RESOURCES.CANDIDATE_ATTACHMENT_DETAIL);
  const canCreateAttachments = ability.can(ACTIONS.CREATE, RESOURCES.CANDIDATE_ATTACHMENT_DETAIL);
  const canDeleteAttachments = ability.can(ACTIONS.DELETE, RESOURCES.CANDIDATE_ATTACHMENT_DETAIL);
  const canReadInterviews = ability.can(ACTIONS.READ, RESOURCES.CANDIDATE_INTERVIEW_SCHEDULE) || ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_MODULE);
  const canReadSkills = ability.can(ACTIONS.READ, RESOURCES.CANDIDATE) || ability.can(ACTIONS.READ, RESOURCES.CANDIDATE_LIST) || ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_MODULE);
  const canReadExperiences = ability.can(ACTIONS.READ, RESOURCES.CANDIDATE) || ability.can(ACTIONS.READ, RESOURCES.CANDIDATE_LIST) || ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_MODULE);
  const canManageExperiences = ability.can(ACTIONS.CREATE, RESOURCES.CANDIDATE);
  const canReadOffers = ability.can(ACTIONS.READ, RESOURCES.CANDIDATE_OFFER);

  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [assessmentRating, setAssessmentRating] = useState(0);
  const [assessmentNotesInput, setAssessmentNotesInput] = useState("");
  const [savingAssessment, setSavingAssessment] = useState(false);
  const [showDeleteNote, setShowDeleteNote] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<number | null>(null);
  const [deletingNote, setDeletingNote] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [hasOnboardingDraft, setHasOnboardingDraft] = useState(false);

  // Schedule Interview dialog
  const [showScheduleInterview, setShowScheduleInterview] = useState(false);
  const [schedulingInterview, setSchedulingInterview] = useState(false);
  const [interviewType, setInterviewType] = useState<"PHONE" | "VIDEO" | "IN_PERSON">("PHONE");
  const [interviewDate, setInterviewDate] = useState("");
  const [interviewTime, setInterviewTime] = useState("09:00");
  const [interviewDuration, setInterviewDuration] = useState(60);
  const [interviewLocation, setInterviewLocation] = useState("");
  const [interviewLink, setInterviewLink] = useState("");
  const [editingInterviewId, setEditingInterviewId] = useState<number | null>(null);

  // Skip Interview dialog
  const [showSkipInterview, setShowSkipInterview] = useState(false);
  const [skippingInterview, setSkippingInterview] = useState(false);

  // Complete Interview dialog
  const [showCompleteInterview, setShowCompleteInterview] = useState(false);
  const [completingInterview, setCompletingInterview] = useState(false);
  const [completeInterviewId, setCompleteInterviewId] = useState<number | null>(null);
  const [interviewFeedback, setInterviewFeedback] = useState("");
  const [interviewRating, setInterviewRating] = useState(3);
  const [interviewOutcome, setInterviewOutcome] = useState<"PASS" | "FAIL" | "PENDING">("PENDING");
  const [interviewRecommendation, setInterviewRecommendation] = useState<"HIRE" | "NO_HIRE" | "CONSIDER" | "">("");
  const isViewer = useMemo(() => {
    if (!candidate || !currentUser) return false;
    return candidate.requisition?.requisitionViewers?.some(
      (rv) => String(rv.user.id) === String(currentUser.id)
    ) ?? false;
  }, [candidate, currentUser]);

  const navigateToEdit = () => {
    if (candidate) {
      navigate(`/recruitment/candidates/${candidate.id}/edit`);
    }
  };

  // Upload attachment dialog
  const [showUploadAttachment, setShowUploadAttachment] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<Attachment | null>(null);
  const [showDeleteAttachment, setShowDeleteAttachment] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState(false);
  const [attachmentSignedUrls, setAttachmentSignedUrls] = useState<Record<number, string>>({});
  const [openingAttachmentId, setOpeningAttachmentId] = useState<number | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Experience dialog state
  const [showExperienceDialog, setShowExperienceDialog] = useState(false);
  const [savingExperience, setSavingExperience] = useState(false);
  const [editingExperienceId, setEditingExperienceId] = useState<number | null>(null);
  const [expRoleTitle, setExpRoleTitle] = useState("");
  const [expCompanyName, setExpCompanyName] = useState("");
  const [expStartDate, setExpStartDate] = useState("");
  const [expEndDate, setExpEndDate] = useState("");
  const [expResponsibilities, setExpResponsibilities] = useState("");
  const [experienceToDelete, setExperienceToDelete] = useState<Experience | null>(null);
  const [showDeleteExperience, setShowDeleteExperience] = useState(false);
  const [deletingExperience, setDeletingExperience] = useState(false);

  // Fetch all candidate data
  const fetchData = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      // Main candidate fetch — required; page blocks if this fails
      const candRes = await api.get<{ success: boolean; candidate: Candidate }>(`/api/candidates/${id}`);
      if (!candRes.success) throw new Error("Failed to load candidate");
      setCandidate(candRes.candidate);

      // Accessory data — fetched independently so one 403 doesn't break the page
      if (canReadNotes) {
        api.get<{ success: boolean; notes: Note[] }>(`/api/candidates/${id}/notes`)
          .then((res) => { if (res.success) setNotes(res.notes); })
          .catch((err) => console.error("Failed to load notes:", err));
      }
      if (canReadSkills) {
        api.get<{ success: boolean; skills: Skill[] }>(`/api/candidates/${id}/skills`)
          .then((res) => { if (res.success) setSkills(res.skills); })
          .catch((err) => console.error("Failed to load skills:", err));
      }
      if (canReadInterviews) {
        api.get<{ success: boolean; interviews: Interview[] }>(`/api/interviews?candidateId=${id}`)
          .then((res) => { if (res.success) setInterviews(res.interviews); })
          .catch((err) => console.error("Failed to load interviews:", err));
      }
      if (canReadAttachments) {
        api.get<{ success: boolean; attachments: Attachment[] }>(`/api/candidates/${id}/attachments`)
          .then((res) => { if (res.success) setAttachments(res.attachments); })
          .catch((err) => console.error("Failed to load attachments:", err));
      }
      if (canReadExperiences) {
        api.get<{ success: boolean; experiences: Experience[] }>(`/api/candidates/${id}/experiences`)
          .then((res) => { if (res.success) setExperiences(res.experiences); })
          .catch((err) => console.error("Failed to load experiences:", err));
      }
      if (canReadOffers) {
        api.get<{ success: boolean; offers: Offer[]; pagination: { total: number } }>(`/api/offers?candidateId=${id}&limit=100`)
          .then((res) => { if (res.success) setOffers(res.offers); })
          .catch((err) => console.error("Failed to load offers:", err));
      }
      // Check for onboarding draft
      api.get<{ success: boolean; data: unknown | null }>(`/api/onboarding/draft/${id}`, { skipErrorToast: true })
        .then((res) => { if (res.success && res.data) setHasOnboardingDraft(true); })
        .catch((err) => console.error("Failed to check onboarding draft:", err));
    } catch (err: any) {
      console.error("Failed to load candidate:", err);
      const msg = err.status === 403
        ? "You don't have permission to view this candidate."
        : err.message || "Failed to load candidate details";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const allowedNextStatuses = useMemo(() => {
    return getAllowedNextStatuses(candidate?.status);
  }, [candidate?.status]);

  const averageInterviewRating = useMemo(() => {
    const rated = interviews.filter((i) => i.rating !== null);
    if (rated.length === 0) return null;
    return Math.round((rated.reduce((sum, i) => sum + (i.rating || 0), 0) / rated.length) * 10) / 10;
  }, [interviews]);

  const displayRating = candidate?.overallRating ?? averageInterviewRating ?? null;

  const handleStatusChange = async (newStatus: string): Promise<boolean> => {
    if (!candidate || newStatus === candidate.status) return false;
    setUpdatingStatus(true);
    try {
      const payload: any = { status: newStatus };
      if (newStatus === "REJECTED") payload.rejectionReason = rejectionReason.trim() || "Status changed to rejected";
      let res;
      if (canManageStatus) {
        res = await api.post<{ success: boolean; candidate: Candidate }>(
          `/api/candidates/${candidate.id}/manage/status-management`,
          payload
        );
      } else {
        res = await api.patch<{ success: boolean; candidate: Candidate }>(
          `/api/candidates/${candidate.id}/status`,
          payload
        );
      }
      if (res.success) {
        setCandidate(res.candidate);
        toast.success(`Status updated to ${formatCandidateStatus(newStatus)}`);
        return true;
      }
      return false;
    } catch (err: any) {
      toast.error(err.message || "Failed to update status");
      return false;
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleReject = async () => {
    if (!candidate) return;
    if (!rejectionReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    setRejecting(true);
    try {
      const endpoint = canManageReject
        ? `/api/candidates/${candidate.id}/manage/reject`
        : `/api/candidates/${candidate.id}/reject`;
      const res = await api.post<{ success: boolean; candidate: Candidate }>(
        endpoint,
        { rejectionReason: rejectionReason.trim() }
      );
      if (res.success) {
        setCandidate(res.candidate);
        toast.success("Candidate rejected");
        setShowReject(false);
        setRejectionReason("");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to reject candidate");
    } finally {
      setRejecting(false);
    }
  };

  const handleSkipInterview = async () => {
    if (!candidate) return;
    setSkippingInterview(true);
    const success = await handleStatusChange("PRE_EMPLOYMENT_COMMENCED");
    setSkippingInterview(false);
    if (success) {
      setShowSkipInterview(false);
    }
  };

  const resetInterviewForm = () => {
    setInterviewType("PHONE");
    setInterviewDate("");
    setInterviewTime("09:00");
    setInterviewDuration(60);
    setInterviewLocation("");
    setInterviewLink("");
    setEditingInterviewId(null);
  };

  const openEditInterview = (interview: Interview) => {
    setEditingInterviewId(interview.id);
    setInterviewType(interview.interviewType as "PHONE" | "VIDEO" | "IN_PERSON");
    const d = new Date(interview.scheduledAt);
    setInterviewDate(d.toISOString().split("T")[0]);
    setInterviewTime(d.toTimeString().slice(0, 5));
    setInterviewDuration(interview.durationMinutes);
    setInterviewLocation(interview.location || "");
    setInterviewLink(interview.meetingLink || "");
    setShowScheduleInterview(true);
  };

  const handleScheduleInterview = async () => {
    if (!candidate) return;
    if (!interviewDate) {
      toast.error("Please select an interview date");
      return;
    }
    setSchedulingInterview(true);
    try {
      const scheduledAt = new Date(`${interviewDate}T${interviewTime}`).toISOString();
      if (editingInterviewId) {
        const res = await api.put<{ success: boolean; interview: Interview }>(`/api/interviews/${editingInterviewId}`, {
          interviewType,
          scheduledAt,
          durationMinutes: interviewDuration,
          location: interviewLocation || null,
          meetingLink: interviewLink || null,
        });
        if (res.success) {
          setInterviews((prev) => prev.map((i) => (i.id === editingInterviewId ? res.interview : i)));
          toast.success("Interview updated successfully");
          setShowScheduleInterview(false);
          resetInterviewForm();
        }
      } else {
        const res = await api.post<{ success: boolean; interview: Interview }>("/api/interviews", {
          candidateId: candidate.id,
          interviewType,
          scheduledAt,
          durationMinutes: interviewDuration,
          location: interviewLocation || null,
          meetingLink: interviewLink || null,
        });
        if (res.success) {
          setInterviews((prev) => [...prev, res.interview]);
          // Refresh candidate since status may have changed
          const candRes = await api.get<{ success: boolean; candidate: Candidate }>(`/api/candidates/${candidate.id}`);
          if (candRes.success) setCandidate(candRes.candidate);
          toast.success("Interview booked successfully");
          setShowScheduleInterview(false);
          resetInterviewForm();
        }
      }
    } catch (err: any) {
      toast.error(err.message || `Failed to ${editingInterviewId ? "update" : "schedule"} interview`);
    } finally {
      setSchedulingInterview(false);
    }
  };

  const handleDeleteInterview = async (interviewId: number) => {
    if (!confirm("Are you sure you want to delete this interview?")) return;
    try {
      await api.delete(`/api/interviews/${interviewId}`);
      setInterviews((prev) => prev.filter((i) => i.id !== interviewId));
      toast.success("Interview deleted");
    } catch (err: any) {
      toast.error(err.message || "Failed to delete interview");
    }
  };

  const handleCompleteInterview = async () => {
    if (!completeInterviewId) return;
    setCompletingInterview(true);
    try {
      const res = await api.patch<{ success: boolean; interview: Interview }>(
        `/api/interviews/${completeInterviewId}/complete`,
        {
          feedback: interviewFeedback.trim() || null,
          rating: interviewRating,
          outcome: interviewOutcome,
          recommendation: interviewRecommendation || null,
        }
      );
      if (res.success) {
        setInterviews((prev) =>
          prev.map((i) => (i.id === completeInterviewId ? res.interview : i))
        );
        toast.success("Interview completed");
        setShowCompleteInterview(false);
        setCompleteInterviewId(null);
        setInterviewFeedback("");
        setInterviewRating(3);
        setInterviewOutcome("PENDING");
        setInterviewRecommendation("");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to complete interview");
    } finally {
      setCompletingInterview(false);
    }
  };

  const handleAddNote = async () => {
    if (!candidate || !noteContent.trim()) return;
    setAddingNote(true);
    try {
      const res = await api.post<{ success: boolean; note: Note }>(
        `/api/candidates/${candidate.id}/notes`,
        { content: noteContent.trim() }
      );
      if (res.success) {
        setNotes((prev) => [res.note, ...prev]);
        toast.success("Note added");
        setShowAddNote(false);
        setNoteContent("");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const openAssessmentDialog = () => {
    if (!candidate) return;
    setAssessmentRating(candidate.overallRating ?? 0);
    setAssessmentNotesInput(candidate.assessmentNotes || "");
    setShowAssessment(true);
  };

  const handleSaveAssessment = async () => {
    if (!candidate) return;
    setSavingAssessment(true);
    try {
      const res = await api.patch<{ success: boolean; candidate: Candidate }>(
        `/api/candidates/${candidate.id}/assessment`,
        {
          overallRating: assessmentRating > 0 ? assessmentRating : null,
          assessmentNotes: assessmentNotesInput.trim() || null,
        }
      );
      if (res.success) {
        setCandidate(res.candidate);
        toast.success("Assessment saved");
        setShowAssessment(false);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save assessment");
    } finally {
      setSavingAssessment(false);
    }
  };

  const handleDeleteNote = (noteId: number) => {
    setNoteToDelete(noteId);
    setShowDeleteNote(true);
  };

  const confirmDeleteNote = async () => {
    if (!candidate || !noteToDelete) return;
    setDeletingNote(true);
    try {
      await api.delete(`/api/candidates/${candidate.id}/notes/${noteToDelete}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteToDelete));
      toast.success("Note deleted");
      setShowDeleteNote(false);
      setNoteToDelete(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to delete note");
    } finally {
      setDeletingNote(false);
    }
  };

  const handleUploadAttachment = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!candidate || !e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post<{ success: boolean; attachment: Attachment }>(
        `/api/candidates/${candidate.id}/attachments`,
        formData
      );
      if (res.success) {
        setAttachments((prev) => [res.attachment, ...prev]);
        toast.success("Document uploaded");
        setShowUploadAttachment(false);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to upload document");
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteAttachment = (attachment: Attachment) => {
    setAttachmentToDelete(attachment);
    setShowDeleteAttachment(true);
  };

  const confirmDeleteAttachment = async () => {
    if (!candidate || !attachmentToDelete) return;
    setDeletingAttachment(true);
    try {
      await api.delete(`/api/candidates/${candidate.id}/attachments/${attachmentToDelete.id}`);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentToDelete.id));
      setAttachmentSignedUrls((prev) => {
        const next = { ...prev };
        delete next[attachmentToDelete.id];
        return next;
      });
      toast.success("Document deleted");
      setShowDeleteAttachment(false);
      setAttachmentToDelete(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to delete document");
    } finally {
      setDeletingAttachment(false);
    }
  };

  const handleViewAttachment = async (attachment: Attachment) => {
    if (attachmentSignedUrls[attachment.id]) {
      window.open(attachmentSignedUrls[attachment.id], "_blank");
      return;
    }
    if (!candidate) return;
    setOpeningAttachmentId(attachment.id);
    try {
      const res = await api.get<{ success: boolean; url: string }>(
        `/api/candidates/${candidate.id}/attachments/${attachment.id}/download`
      );
      if (res.success && res.url) {
        setAttachmentSignedUrls((prev) => ({ ...prev, [attachment.id]: res.url }));
        window.open(res.url, "_blank");
      } else {
        toast.error("Failed to get document URL");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to get document URL");
    } finally {
      setOpeningAttachmentId(null);
    }
  };

  const resetExperienceForm = () => {
    setEditingExperienceId(null);
    setExpRoleTitle("");
    setExpCompanyName("");
    setExpStartDate("");
    setExpEndDate("");
    setExpResponsibilities("");
  };

  const openEditExperience = (exp: Experience) => {
    setEditingExperienceId(exp.id);
    setExpRoleTitle(exp.roleTitle);
    setExpCompanyName(exp.companyName || "");
    setExpStartDate(exp.startDate || "");
    setExpEndDate(exp.endDate || "");
    setExpResponsibilities(exp.responsibilities || "");
    setShowExperienceDialog(true);
  };

  const handleSaveExperience = async () => {
    if (!candidate) return;
    if (!expRoleTitle.trim()) {
      toast.error("Role title is required");
      return;
    }
    setSavingExperience(true);
    try {
      const payload = {
        roleTitle: expRoleTitle.trim(),
        companyName: expCompanyName.trim() || null,
        startDate: expStartDate || null,
        endDate: expEndDate || null,
        responsibilities: expResponsibilities.trim() || null,
      };
      if (editingExperienceId) {
        const res = await api.put<{ success: boolean; experience: Experience }>(
          `/api/candidates/${candidate.id}/experiences/${editingExperienceId}`,
          payload
        );
        if (res.success) {
          setExperiences((prev) => prev.map((e) => (e.id === editingExperienceId ? res.experience : e)));
          toast.success("Experience updated");
          setShowExperienceDialog(false);
          resetExperienceForm();
        }
      } else {
        const res = await api.post<{ success: boolean; experience: Experience }>(
          `/api/candidates/${candidate.id}/experiences`,
          payload
        );
        if (res.success) {
          setExperiences((prev) => [res.experience, ...prev]);
          toast.success("Experience added");
          setShowExperienceDialog(false);
          resetExperienceForm();
        }
      }
    } catch (err: any) {
      toast.error(err.message || `Failed to ${editingExperienceId ? "update" : "add"} experience`);
    } finally {
      setSavingExperience(false);
    }
  };

  const handleDeleteExperience = (exp: Experience) => {
    setExperienceToDelete(exp);
    setShowDeleteExperience(true);
  };

  const confirmDeleteExperience = async () => {
    if (!candidate || !experienceToDelete) return;
    setDeletingExperience(true);
    try {
      await api.delete(`/api/candidates/${candidate.id}/experiences/${experienceToDelete.id}`);
      setExperiences((prev) => prev.filter((e) => e.id !== experienceToDelete.id));
      toast.success("Experience deleted");
      setShowDeleteExperience(false);
      setExperienceToDelete(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to delete experience");
    } finally {
      setDeletingExperience(false);
    }
  };

  const formatExperienceDates = (start: string | null, end: string | null) => {
    if (!start && !end) return "—";
    const s = start ? formatSydneyDateShort(start) : "?";
    const e = end ? formatSydneyDateShort(end) : "Present";
    return `${s} – ${e}`;
  };

  if (!canReadCandidate) {
    return <AccessRestricted />;
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <Loader2 size={32} className="animate-spin text-gray-400" />
        <p className="mt-3 text-sm font-medium text-gray-500">Loading candidate...</p>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <p className="text-sm font-medium text-gray-500">{error || "Candidate not found."}</p>
        <div className="flex items-center gap-3 mt-4">
          {error && (
            <Button variant="outline" className="rounded-xl" onClick={fetchData}>
              Retry
            </Button>
          )}
          <Button variant="outline" className="rounded-xl" onClick={() => navigate("/recruitment")}>
            Back to Recruitment
          </Button>
        </div>
      </div>
    );
  }

  const requisitionTitle = candidate.requisition?.jobRole?.roleName || "Unknown Position";

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Back Button */}
      <button
        onClick={() => navigate(`/recruitment/requisitions/${candidate.requisition?.id}`)}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">Back to {requisitionTitle}</span>
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 font-bold text-lg shrink-0">
            {getInitials(candidate.firstName, candidate.lastName)}
          </div>
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              {candidate.firstName} {candidate.lastName}
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant="outline"
                className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getStatusBadgeStyle(candidate.status)}`}
              >
                {formatCandidateStatus(candidate.status)}
              </Badge>
              {candidate.source && (
                <Badge
                  variant="outline"
                  className="rounded-lg border border-gray-200 font-bold px-2.5 py-1 text-[10px] bg-white text-gray-700"
                >
                  {candidate.source}
                </Badge>
              )}
              <span className="text-sm text-gray-500 font-medium">
                Applied {formatShortDate(candidate.createdAt)}
              </span>
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {canManageCandidate && (
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 h-10 rounded-xl text-sm font-bold"
              onClick={navigateToEdit}
            >
              <Pencil size={16} />
              <span>Edit Candidate</span>
            </Button>
          )}
          <Button
            className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold"
            onClick={() => setShowAddNote(true)}
          >
            <MessageSquare size={16} />
            <span>Add Note</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Experience", value: candidate.yearsExperience ? `${candidate.yearsExperience} years` : "—", icon: <Briefcase size={22} />, color: "text-blue-500", bg: "bg-blue-50" },
          { label: "Skills", value: skills.length, icon: <Award size={22} />, color: "text-green-500", bg: "bg-green-50" },
          { label: "Interviews", value: interviews.length, icon: <CalendarDays size={22} />, color: "text-purple-500", bg: "bg-purple-50" },
          { label: "Overall Rating", value: displayRating ? `${displayRating}/10` : "—", icon: <Star size={22} />, color: "text-amber-500", bg: "bg-amber-50" },
        ].map((stat) => (
          <div key={stat.label} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
            <div className="flex justify-between items-start">
              <div className="z-10">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{stat.label}</p>
                <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">{stat.value}</h3>
              </div>
              <div className={`p-2.5 rounded-xl transition-colors z-10 ${stat.bg}`}>
                <div className={`${stat.color} transition-colors`}>{stat.icon}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-full w-fit hidden md:flex h-auto mb-2">
          <TabsTrigger value="overview" className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all">Overview</TabsTrigger>
          <TabsTrigger value="experience" className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all">Experience</TabsTrigger>
          {canReadInterviews && (
            <TabsTrigger value="interviews" className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all">Interviews ({interviews.length})</TabsTrigger>
          )}
          {canReadNotes && (
            <TabsTrigger value="notes" className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all">Notes ({notes.length})</TabsTrigger>
          )}
          {canReadAttachments && (
            <TabsTrigger value="documents" className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all">Documents ({attachments.length})</TabsTrigger>
          )}
          {canReadOffers && (
            <TabsTrigger value="offers" className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all">Offers ({offers.length})</TabsTrigger>
          )}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Personal Information */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-5">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-gray-900 text-base">Personal Information</h4>
                  {!isViewer && (
                    <button onClick={navigateToEdit} className="text-gray-400 hover:text-gray-600 transition-colors">
                      <Pencil size={14} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  <div className="flex items-start gap-3">
                    <Mail size={16} className="text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Email</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{candidate.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone size={16} className="text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Phone</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{candidate.phone ?? "—"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Address</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{candidate.address ?? "—"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Linkedin size={16} className="text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">LinkedIn</p>
                      {candidate.linkedInProfile ? (
                        <a href={candidate.linkedInProfile} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-blue-600 hover:underline mt-0.5">View Profile</a>
                      ) : (
                        <p className="text-sm font-bold text-gray-900 mt-0.5">—</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Position */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-5">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-gray-900 text-base">Current Position</h4>
                  {!isViewer && (
                    <button onClick={navigateToEdit} className="text-gray-400 hover:text-gray-600 transition-colors">
                      <Pencil size={14} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Position</p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">{candidate.currentPosition ?? "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Company</p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">{candidate.currentCompany ?? "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Experience</p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">{candidate.yearsExperience ? `${candidate.yearsExperience} years` : "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Applied For</p>
                    <p className="text-sm font-bold text-gray-900 mt-0.5">{requisitionTitle}</p>
                  </div>
                </div>
              </div>

              {/* Technical Skills */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
                <h4 className="font-bold text-gray-900 text-base">Technical Skills</h4>
                {skills.length === 0 ? (
                  <p className="text-sm text-gray-500 font-medium">No skills recorded.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {skills.map((skill) => (
                      <Badge
                        key={skill.id}
                        variant="outline"
                        className="rounded-full border border-gray-200 font-bold px-3 py-1 text-xs bg-white text-gray-700"
                      >
                        {skill.skillName}
                        {skill.proficiencyLevel && (
                          <span className="ml-1 text-gray-400 font-normal">· {skill.proficiencyLevel}</span>
                        )}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Past Experience */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-gray-900 text-base">Past Experience</h4>
                  {!isViewer && canManageExperiences && (
                    <button
                      onClick={() => { resetExperienceForm(); setShowExperienceDialog(true); }}
                      className="text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      + Add
                    </button>
                  )}
                </div>
                {experiences.length === 0 ? (
                  <p className="text-sm text-gray-500 font-medium">No past experience recorded.</p>
                ) : (
                  <div className="space-y-3">
                    {experiences.slice(0, 3).map((exp) => (
                      <div key={exp.id} className="space-y-1">
                        <p className="text-sm font-bold text-gray-900">{exp.roleTitle}</p>
                        {exp.companyName && <p className="text-xs text-gray-500 font-medium">{exp.companyName}</p>}
                        <p className="text-xs text-gray-400 font-medium">{formatExperienceDates(exp.startDate, exp.endDate)}</p>
                      </div>
                    ))}
                    {experiences.length > 3 && (
                      <button
                        onClick={() => setActiveTab("experience")}
                        className="text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        View all {experiences.length} experiences
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Overall Assessment */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-gray-900 text-base">Overall Assessment</h4>
                  {isViewer ? (
                    <button
                      onClick={openAssessmentDialog}
                      title="Leave an assessment"
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <Pencil size={14} />
                    </button>
                  ) : (
                    <button onClick={navigateToEdit} className="text-gray-400 hover:text-gray-600 transition-colors">
                      <Pencil size={14} />
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Rating:</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <Star
                          key={n}
                          size={14}
                          className={n <= (displayRating || 0) ? "text-amber-400 fill-amber-400" : "text-gray-200"}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-bold text-gray-900">{displayRating ? `${displayRating}/10` : "—"}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Notes:</span>
                    <p className="text-sm font-medium text-gray-700 mt-1">
                      {candidate.assessmentNotes || "No assessment notes yet."}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-1 space-y-6">
              {canManageCandidate && (
              <>
              {/* Quick Actions */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-3">
                <h4 className="font-bold text-gray-900 text-base">Quick Actions</h4>
                {candidate.status === "PHONE_SCREEN" && (
                  <>
                    {canScheduleInterview && (
                      <button
                        className="w-full flex items-center gap-3 p-3 rounded-xl bg-purple-600 hover:bg-purple-700 transition-colors text-left group"
                        onClick={() => setShowScheduleInterview(true)}
                      >
                        <Video size={18} className="text-white" />
                        <span className="text-sm font-bold text-white">Book Interview</span>
                      </button>
                    )}
                    <button
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-700 hover:bg-slate-800 transition-colors text-left group"
                      onClick={() => setShowSkipInterview(true)}
                    >
                      <CheckCircle size={18} className="text-white" />
                      <span className="text-sm font-bold text-white">Skip Interview</span>
                    </button>
                  </>
                )}
                {(candidate.status === "INTERVIEW" || candidate.status === "PRE_EMPLOYMENT_COMPLETED" || candidate.status === "DRAFT_OFFER" || candidate.status === "OFFER_SENT" || candidate.status === "OFFER_ACCEPTED" || candidate.status === "OFFER_EXPIRED" || candidate.status === "OFFER_REJECTED") && canManageExtendOffer && (
                  <button
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-950 hover:bg-slate-900 transition-colors text-left group"
                    onClick={() => navigate(`/recruitment/candidates/${candidate.id}/offer`)}
                  >
                    <FileText size={18} className="text-white" />
                    <span className="text-sm font-bold text-white">
                      {candidate.status === "INTERVIEW" || candidate.status === "PRE_EMPLOYMENT_COMPLETED"
                        ? "Extend Offer"
                        : candidate.status === "DRAFT_OFFER"
                        ? "Edit Draft Offer"
                        : "Resend Offer"}
                    </span>
                  </button>
                )}
                {candidate.status === "OFFER_ACCEPTED" && (
                  <button
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left group ${
                      hasOnboardingDraft
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-green-600 hover:bg-green-700"
                    }`}
                    onClick={() => navigate(`/onboarding/commence/${candidate.id}`)}
                  >
                    {hasOnboardingDraft ? (
                      <>
                        <PlayCircle size={18} className="text-white" />
                        <span className="text-sm font-bold text-white">Resume Hiring</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} className="text-white" />
                        <span className="text-sm font-bold text-white">Hire Candidate</span>
                      </>
                    )}
                  </button>
                )}
                {canManageReject && !["HIRING_COMMENCED", "HIRED", "REJECTED"].includes(candidate.status) && (
                  <button
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-red-50 hover:border-red-100 transition-colors text-left group"
                    onClick={() => setShowReject(true)}
                  >
                    <XCircle size={18} className="text-red-500" />
                    <span className="text-sm font-medium text-red-600 group-hover:text-red-700">Reject Candidate</span>
                  </button>
                )}
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-left"
                  onClick={navigateToEdit}
                >
                  <Pencil size={18} className="text-gray-500" />
                  <span className="text-sm font-medium text-gray-900">Edit Candidate</span>
                </button>
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-left"
                  onClick={() => setShowAddNote(true)}
                >
                  <MessageSquare size={18} className="text-gray-500" />
                  <span className="text-sm font-medium text-gray-900">Add Note</span>
                </button>
              </div>

              {/* Status Management */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
                <div className="flex items-center gap-2">
                  <Award size={18} className="text-blue-500" />
                  <h4 className="font-bold text-gray-900 text-base">Status Management</h4>
                </div>
                <p className="text-gray-500 text-sm font-medium">
                  Use Quick Actions for interview scheduling and other automated transitions.
                </p>

                <div className="p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                  <Badge
                    variant="outline"
                    className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getStatusBadgeStyle(candidate.status)}`}
                  >
                    {formatCandidateStatus(candidate.status)}
                  </Badge>
                </div>

                <div className="space-y-1">
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Current Status:</p>
                  <p className="text-sm font-medium text-gray-600">{getStatusDescription(candidate.status)}</p>
                </div>

                {(canManageStatus || canPatchStatus) && allowedNextStatuses.length > 0 && !["DRAFT_OFFER", "OFFER_SENT", "OFFER_ACCEPTED", "OFFER_REJECTED", "OFFER_EXPIRED", "HIRING_COMMENCED", "HIRED"].includes(candidate.status) && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Change Status:</p>
                    <Select value={candidate.status} onValueChange={handleStatusChange} disabled={updatingStatus}>
                      <SelectTrigger className="bg-white border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        <SelectItem value={candidate.status}>
                          {formatCandidateStatus(candidate.status)} (Current)
                        </SelectItem>
                        {allowedNextStatuses.map((s) => (
                          <SelectItem key={s} value={s}>
                            {formatCandidateStatus(s)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-gray-400 font-medium">
                      Only manual transitions are shown. Schedule Interview and Hire actions are in Quick Actions above.
                    </p>
                  </div>
                )}
              </div>
              </>
              )}

              {/* Application Timeline */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
                <h4 className="font-bold text-gray-900 text-base">Application Timeline</h4>
                <div className="space-y-0">
                  {[
                    { icon: <UserPlus size={14} className="text-blue-500" />, iconBg: "bg-blue-50", title: "Application Submitted", date: formatDate(candidate.createdAt) },
                    ...(candidate.status !== "SHORTLISTED" ? [{ icon: <CheckCircle size={14} className="text-green-500" />, iconBg: "bg-green-50", title: "Status Updated", date: `Changed to ${formatCandidateStatus(candidate.status)}` }] : []),
                    ...(interviews.length > 0 ? [{ icon: <CalendarDays size={14} className="text-purple-500" />, iconBg: "bg-purple-50", title: "Interviews", date: `${interviews.length} scheduled` }] : []),
                  ].map((event, index, arr) => (
                    <div key={index}>
                      <div className="flex items-start gap-3 py-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${event.iconBg}`}>
                          {event.icon}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">{event.title}</p>
                          <p className="text-xs text-gray-500 font-medium mt-0.5">{event.date}</p>
                        </div>
                      </div>
                      {index < arr.length - 1 && <div className="ml-3.5 pl-3.5 border-l border-gray-100 h-4" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Offers Tab */}
        <TabsContent value="offers" className="mt-0 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-gray-900 text-base">Offer History</h4>
          </div>
          {offers.length === 0 ? (
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm text-center">
              <p className="text-sm font-medium text-gray-500">No offers yet.</p>
            </div>
          ) : (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Header row (hidden on mobile) */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="col-span-2 text-xs font-bold text-gray-400 uppercase tracking-widest">Status</div>
                <div className="col-span-2 text-xs font-bold text-gray-400 uppercase tracking-widest">Sent</div>
                <div className="col-span-2 text-xs font-bold text-gray-400 uppercase tracking-widest">Salary</div>
                <div className="col-span-2 text-xs font-bold text-gray-400 uppercase tracking-widest">Start Date</div>
                <div className="col-span-2 text-xs font-bold text-gray-400 uppercase tracking-widest">Expiry</div>
                <div className="col-span-2 text-xs font-bold text-gray-400 uppercase tracking-widest text-right">Actions</div>
              </div>
              <div className="divide-y divide-gray-100">
                {offers.map((offer) => (
                  <div
                    key={offer.id}
                    className={`grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-6 py-4 items-center ${offer.supersededAt ? "bg-amber-50/30" : ""}`}
                  >
                    {/* Status */}
                    <div className="md:col-span-2 flex items-center gap-2">
                      <span className="md:hidden text-xs font-bold text-gray-400 uppercase tracking-widest w-16 shrink-0">Status</span>
                      <Badge
                        variant="outline"
                        className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${
                          offer.status === "SENT"
                            ? "bg-blue-50 text-blue-700 border-blue-100"
                            : offer.status === "ACCEPTED"
                            ? "bg-green-50 text-green-700 border-green-100"
                            : offer.status === "REJECTED"
                            ? "bg-red-50 text-red-700 border-red-100"
                            : offer.status === "EXPIRED"
                            ? "bg-gray-100 text-gray-600 border-gray-100"
                            : "bg-orange-50 text-orange-700 border-orange-100"
                        }`}
                      >
                        {offer.status === "SENT"
                          ? "Sent"
                          : offer.status === "ACCEPTED"
                          ? "Accepted"
                          : offer.status === "REJECTED"
                          ? "Rejected"
                          : offer.status === "EXPIRED"
                          ? "Expired"
                          : "Draft"}
                      </Badge>
                      {offer.supersededAt && (
                        <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-md">Superseded</span>
                      )}
                    </div>

                    {/* Sent At */}
                    <div className="md:col-span-2 flex items-center gap-2">
                      <span className="md:hidden text-xs font-bold text-gray-400 uppercase tracking-widest w-16 shrink-0">Sent</span>
                      <span className="text-sm font-bold text-gray-900">
                        {offer.sentAt ? formatShortDate(offer.sentAt) : "—"}
                      </span>
                    </div>

                    {/* Salary */}
                    <div className="md:col-span-2 flex items-center gap-2">
                      <span className="md:hidden text-xs font-bold text-gray-400 uppercase tracking-widest w-16 shrink-0">Salary</span>
                      <span className="text-sm font-bold text-gray-900">
                        {offer.salaryOffered ? `$${Number(offer.salaryOffered).toLocaleString()}` : "—"}
                      </span>
                    </div>

                    {/* Start Date */}
                    <div className="md:col-span-2 flex items-center gap-2">
                      <span className="md:hidden text-xs font-bold text-gray-400 uppercase tracking-widest w-16 shrink-0">Start</span>
                      <span className="text-sm font-medium text-gray-700">
                        {offer.startDate ? formatShortDate(offer.startDate) : "—"}
                      </span>
                    </div>

                    {/* Expiry */}
                    <div className="md:col-span-2 flex items-center gap-2">
                      <span className="md:hidden text-xs font-bold text-gray-400 uppercase tracking-widest w-16 shrink-0">Expiry</span>
                      <span className="text-sm font-medium text-gray-700">
                        {offer.expiryDate ? formatShortDate(offer.expiryDate) : "—"}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="md:col-span-2 flex items-center md:justify-end gap-2">
                      <span className="md:hidden text-xs font-bold text-gray-400 uppercase tracking-widest w-16 shrink-0">Actions</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 rounded-lg text-xs font-bold px-3 bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                        onClick={() => navigate(`/recruitment/offers/${offer.id}`)}
                      >
                        <Eye size={14} className="mr-1.5" />
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Experience Tab */}
        <TabsContent value="experience" className="mt-0 space-y-4">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-900 text-base">Experience Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Current Position</p>
                <p className="text-sm font-bold text-gray-900 mt-1">{candidate.currentPosition ?? "—"}</p>
              </div>
              <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Current Company</p>
                <p className="text-sm font-bold text-gray-900 mt-1">{candidate.currentCompany ?? "—"}</p>
              </div>
              <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Years Experience</p>
                <p className="text-sm font-bold text-gray-900 mt-1">{candidate.yearsExperience ? `${candidate.yearsExperience} years` : "—"}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h4 className="font-bold text-gray-900 text-base">Work History</h4>
            {!isViewer && canManageExperiences && (
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 h-9 rounded-xl text-xs font-bold px-4"
                onClick={() => { resetExperienceForm(); setShowExperienceDialog(true); }}
              >
                <UserPlus size={14} />
                <span>Add Experience</span>
              </Button>
            )}
          </div>

          {experiences.length === 0 ? (
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm text-center">
              <p className="text-sm font-medium text-gray-500">
                {canManageExperiences && !isViewer ? "No work history yet. Add your first experience." : "No work history recorded yet."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {experiences.map((exp) => (
                <div key={exp.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <p className="text-sm font-bold text-gray-900">{exp.roleTitle}</p>
                      {exp.companyName && <p className="text-xs text-gray-500 font-medium">{exp.companyName}</p>}
                      <p className="text-xs text-gray-400 font-medium">{formatExperienceDates(exp.startDate, exp.endDate)}</p>
                    </div>
                    {!isViewer && canManageExperiences && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => openEditExperience(exp)}
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Edit Experience"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteExperience(exp)}
                          className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                          title="Delete Experience"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                  {exp.responsibilities && (
                    <div>
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mb-1">Responsibilities</p>
                      <p className="text-sm font-medium text-gray-700 whitespace-pre-line">{exp.responsibilities}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Interviews Tab */}
        <TabsContent value="interviews" className="mt-0 space-y-4">
          {interviews.length === 0 ? (
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm text-center">
              <p className="text-sm font-medium text-gray-500">No interviews scheduled yet.</p>
            </div>
          ) : (
            interviews.map((interview) => (
              <div key={interview.id} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-gray-900">{interview.interviewType.replace("_", " ")} Interview</p>
                    <p className="text-xs text-gray-500 font-medium mt-0.5">{formatDateTime(interview.scheduledAt)} · {interview.durationMinutes} min</p>
                  </div>
                  <Badge variant="outline" className="rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] bg-gray-100 text-gray-700">
                    {interview.status}
                  </Badge>
                </div>
                {interview.rating !== null && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Rating:</span>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={12} className={n <= (interview.rating || 0) ? "text-amber-400 fill-amber-400" : "text-gray-200"} />
                      ))}
                    </div>
                    <span className="text-xs font-bold text-gray-900">{interview.rating}/5</span>
                  </div>
                )}
                {interview.outcome && interview.outcome !== "PENDING" && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Outcome:</span>
                    <Badge variant="outline" className={`rounded-lg border-0 font-bold px-2 py-0.5 text-[10px] ${interview.outcome === "PASS" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                      {interview.outcome === "PASS" ? "Passed" : "Failed"}
                    </Badge>
                  </div>
                )}
                {interview.recommendation && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Recommendation:</span>
                    <span className="text-xs font-bold text-gray-900">
                      {interview.recommendation === "HIRE" ? "Hire" : interview.recommendation === "NO_HIRE" ? "No Hire" : "Consider"}
                    </span>
                  </div>
                )}
                {interview.feedback && (
                  <div>
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">Feedback:</span>
                    <p className="text-sm font-medium text-gray-700 mt-1">{interview.feedback}</p>
                  </div>
                )}
                {interview.status === "SCHEDULED" && !isViewer && (canCompleteInterview || canUpdateInterview) && (
                  <div className="pt-2 flex items-center gap-2">
                    {canCompleteInterview && (
                      <Button
                        className="bg-purple-600 hover:bg-purple-700 text-white h-9 rounded-xl text-xs font-bold px-4"
                        onClick={() => {
                          setCompleteInterviewId(interview.id);
                          setShowCompleteInterview(true);
                        }}
                      >
                        Complete Interview
                      </Button>
                    )}
                    {canUpdateInterview && (
                      <button
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Edit Interview"
                        onClick={() => openEditInterview(interview)}
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    {canDeleteInterview && (
                      <button
                        className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete Interview"
                        onClick={() => handleDeleteInterview(interview.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="mt-0 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-gray-900 text-base">Notes</h4>
            <Button
              className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 h-9 rounded-xl text-xs font-bold px-4"
              onClick={() => setShowAddNote(true)}
            >
              <MessageSquare size={14} />
              <span>Add Note</span>
            </Button>
          </div>
          {notes.length === 0 ? (
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm text-center">
              <p className="text-sm font-medium text-gray-500">
                {isViewer ? "No notes yet." : "No notes yet. Add your first note."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <div key={note.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      {note.isPinned && <Pin size={14} className="text-blue-500" />}
                      <p className="text-xs text-gray-400 font-bold">{note.createdBy?.name ?? "—"} · {formatShortDate(note.createdAt)}</p>
                    </div>
                    {!isViewer && (
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-700">{note.content}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-0 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-gray-900 text-base">Documents</h4>
            {canCreateAttachments && !isViewer && (
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 h-9 rounded-xl text-xs font-bold px-4"
                onClick={() => setShowUploadAttachment(true)}
              >
                <Upload size={14} />
                <span>Upload Document</span>
              </Button>
            )}
          </div>
          {attachments.length === 0 ? (
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm text-center">
              <p className="text-sm font-medium text-gray-500">
                {canCreateAttachments && !isViewer ? "No documents yet. Upload your first document." : "No documents uploaded yet."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {attachments.map((att) => (
                <div key={att.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center shrink-0">
                      <FileText size={18} className="text-gray-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{att.filename}</p>
                      <p className="text-xs text-gray-500 font-medium">{att.createdBy?.name ?? "—"} · {formatShortDate(att.createdAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleViewAttachment(att)}
                      disabled={openingAttachmentId === att.id}
                      className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                      title="View / Download"
                    >
                      {openingAttachmentId === att.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                    {canDeleteAttachments && !isViewer && (
                      <button
                        onClick={() => handleDeleteAttachment(att)}
                        className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Delete Document"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Note Dialog */}
      <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">Add Note</DialogTitle>
            <p className="text-sm text-gray-500 font-medium">Add a note about {candidate.firstName} {candidate.lastName}</p>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <Textarea
              placeholder="Write your note here..."
              className="bg-gray-50 border-gray-100 rounded-xl text-sm font-medium min-h-[120px] resize-y focus:ring-2 focus:ring-blue-100"
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
            />
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5" onClick={() => setShowAddNote(false)}>
                Cancel
              </Button>
              <Button className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5" onClick={handleAddNote} disabled={addingNote}>
                {addingNote ? "Adding..." : "Add Note"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Overall Assessment Dialog (viewers) */}
      <Dialog open={showAssessment} onOpenChange={setShowAssessment}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">Overall Assessment</DialogTitle>
            <p className="text-sm text-gray-500 font-medium">Leave an assessment for {candidate.firstName} {candidate.lastName}</p>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Overall Rating (1-10)</label>
              <div className="flex items-center gap-1 flex-wrap">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setAssessmentRating(n)}
                    className="p-0.5 transition-colors"
                  >
                    <Star
                      size={22}
                      className={n <= assessmentRating ? "text-amber-400 fill-amber-400" : "text-gray-200"}
                    />
                  </button>
                ))}
                {assessmentRating > 0 && (
                  <span className="ml-2 text-sm font-bold text-gray-900">{assessmentRating}/10</span>
                )}
              </div>
            </div>
            <Textarea
              placeholder="Assessment notes..."
              className="bg-gray-50 border-gray-100 rounded-xl text-sm font-medium min-h-[120px] resize-y focus:ring-2 focus:ring-blue-100"
              value={assessmentNotesInput}
              onChange={(e) => setAssessmentNotesInput(e.target.value)}
            />
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5" onClick={() => setShowAssessment(false)}>
                Cancel
              </Button>
              <Button className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5" onClick={handleSaveAssessment} disabled={savingAssessment}>
                {savingAssessment ? "Saving..." : "Save Assessment"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">Reject Candidate</DialogTitle>
            <p className="text-sm text-gray-500 font-medium">Provide a reason for rejecting {candidate.firstName} {candidate.lastName}</p>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <Textarea
              placeholder="Rejection reason..."
              className="bg-gray-50 border-gray-100 rounded-xl text-sm font-medium min-h-[100px] resize-y focus:ring-2 focus:ring-blue-100"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5" onClick={() => setShowReject(false)}>
                Cancel
              </Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5" onClick={handleReject} disabled={rejecting}>
                {rejecting ? "Rejecting..." : "Reject Candidate"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Interview Dialog */}
      <Dialog open={showScheduleInterview} onOpenChange={(open) => {
        if (!open) resetInterviewForm();
        setShowScheduleInterview(open);
      }}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">
              {editingInterviewId ? "Edit Interview" : "Book Interview"}
            </DialogTitle>
            <p className="text-sm text-gray-500 font-medium">
              {editingInterviewId ? "Update interview details" : `Set up an interview for ${candidate.firstName} ${candidate.lastName}`}
            </p>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            {!editingInterviewId && (
              <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
                <p className="text-sm text-purple-800 font-medium">
                  This will book an interview and transition the candidate status to <strong>Interview</strong>.
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Interview Type</label>
              <Select value={interviewType} onValueChange={(v) => setInterviewType(v as "PHONE" | "VIDEO" | "IN_PERSON")}>
                <SelectTrigger className="h-10 rounded-xl border-gray-200 bg-white text-sm">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="PHONE">Phone</SelectItem>
                  <SelectItem value="VIDEO">Video</SelectItem>
                  <SelectItem value="IN_PERSON">In-Person</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-10 rounded-xl border-gray-200 bg-white text-sm font-normal justify-start text-left"
                  >
                    <CalendarDays size={16} className="mr-2 text-gray-400" />
                    {interviewDate ? interviewDate : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarPrimitive
                    mode="single"
                    selected={interviewDate ? new Date(interviewDate + "T00:00:00") : undefined}
                    onSelect={(date) => {
                      if (date) {
                        const y = date.getFullYear();
                        const m = String(date.getMonth() + 1).padStart(2, "0");
                        const d = String(date.getDate()).padStart(2, "0");
                        setInterviewDate(`${y}-${m}-${d}`);
                      } else {
                        setInterviewDate("");
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Time</label>
              <Input
                type="time"
                value={interviewTime}
                onChange={(e) => setInterviewTime(e.target.value)}
                className="h-10 rounded-xl border-gray-200 bg-white text-sm font-medium"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Duration (minutes)</label>
              <Select value={String(interviewDuration)} onValueChange={(v) => setInterviewDuration(Number(v))}>
                <SelectTrigger className="h-10 rounded-xl border-gray-200 bg-white text-sm">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Location</label>
              <Input
                placeholder="e.g. Meeting Room A, Zoom link, etc."
                value={interviewLocation}
                onChange={(e) => setInterviewLocation(e.target.value)}
                className="h-10 rounded-xl border-gray-200 bg-white text-sm font-medium"
              />
            </div>

            {interviewType === "VIDEO" && (
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Meeting Link</label>
                <Input
                  placeholder="https://zoom.us/j/..."
                  value={interviewLink}
                  onChange={(e) => setInterviewLink(e.target.value)}
                  className="h-10 rounded-xl border-gray-200 bg-white text-sm font-medium"
                />
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button variant="outline" className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5" onClick={() => { resetInterviewForm(); setShowScheduleInterview(false); }}>
                Cancel
              </Button>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white h-10 rounded-xl text-sm font-bold px-5" onClick={handleScheduleInterview} disabled={schedulingInterview}>
                {schedulingInterview ? (editingInterviewId ? "Saving..." : "Booking...") : (editingInterviewId ? "Update Interview" : "Book Interview")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Skip Interview Dialog */}
      <Dialog open={showSkipInterview} onOpenChange={setShowSkipInterview}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">Skip Interview</DialogTitle>
            <p className="text-sm text-gray-500 font-medium">
              You are about to Skip Interviewing this candidate, this will update the requisition status to &apos;PRE EMPLOYMENT COMMENCED&apos;, are you sure you want to continue?
            </p>
          </DialogHeader>
          <div className="px-6 pb-6 flex items-center justify-end gap-3">
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              onClick={() => setShowSkipInterview(false)}
              disabled={skippingInterview}
            >
              Cancel, Go Back
            </Button>
            <Button
              className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={handleSkipInterview}
              disabled={skippingInterview}
            >
              {skippingInterview ? "Updating..." : "Continue, Skip Interview"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Interview Dialog */}
      <Dialog open={showCompleteInterview} onOpenChange={setShowCompleteInterview}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">Complete Interview</DialogTitle>
            <p className="text-sm text-gray-500 font-medium">Record feedback and outcome for this interview</p>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Outcome</label>
              <Select value={interviewOutcome} onValueChange={(v) => setInterviewOutcome(v as "PASS" | "FAIL" | "PENDING")}>
                <SelectTrigger className="h-10 rounded-xl border-gray-200 bg-white text-sm">
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="PASS">Pass</SelectItem>
                  <SelectItem value="FAIL">Fail</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Rating (1–5)</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setInterviewRating(n)}
                    className="p-1 transition-colors"
                  >
                    <Star
                      size={24}
                      className={n <= interviewRating ? "text-amber-400 fill-amber-400" : "text-gray-200"}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Recommendation</label>
              <Select value={interviewRecommendation || "none"} onValueChange={(v) => setInterviewRecommendation(v === "none" ? "" : v as "HIRE" | "NO_HIRE" | "CONSIDER")}>
                <SelectTrigger className="h-10 rounded-xl border-gray-200 bg-white text-sm">
                  <SelectValue placeholder="Select recommendation" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="HIRE">Hire</SelectItem>
                  <SelectItem value="NO_HIRE">No Hire</SelectItem>
                  <SelectItem value="CONSIDER">Consider</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Feedback</label>
              <Textarea
                placeholder="Interview feedback, strengths, concerns..."
                value={interviewFeedback}
                onChange={(e) => setInterviewFeedback(e.target.value)}
                className="bg-gray-50 border-gray-100 rounded-xl text-sm font-medium min-h-[100px] resize-y focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button variant="outline" className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5" onClick={() => setShowCompleteInterview(false)}>
                Cancel
              </Button>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white h-10 rounded-xl text-sm font-bold px-5" onClick={handleCompleteInterview} disabled={completingInterview}>
                {completingInterview ? "Saving..." : "Save Results"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Note Dialog */}
      <Dialog open={showDeleteNote} onOpenChange={setShowDeleteNote}>
        <DialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">Delete Note</DialogTitle>
            <p className="text-sm text-gray-500 font-medium">
              Are you sure you want to delete this note? This action cannot be undone.
            </p>
          </DialogHeader>
          <div className="px-6 pb-6 flex items-center justify-end gap-3">
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              onClick={() => setShowDeleteNote(false)}
              disabled={deletingNote}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={confirmDeleteNote}
              disabled={deletingNote}
            >
              {deletingNote ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Attachment Dialog */}
      <Dialog open={showUploadAttachment} onOpenChange={setShowUploadAttachment}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">Upload Document</DialogTitle>
            <p className="text-sm text-gray-500 font-medium">Select a file to upload for this candidate</p>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-200 rounded-2xl p-8 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                <Upload size={22} className="text-gray-500" />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-gray-900">Click to upload</p>
                <p className="text-xs text-gray-500 font-medium mt-1">PDF, Word, images, or any file type</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleUploadAttachment}
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="outline"
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                onClick={() => setShowUploadAttachment(false)}
                disabled={uploadingAttachment}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Attachment Dialog */}
      <Dialog open={showDeleteAttachment} onOpenChange={setShowDeleteAttachment}>
        <DialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">Delete Document</DialogTitle>
            <p className="text-sm text-gray-500 font-medium">
              Are you sure you want to delete <span className="font-bold text-gray-700">{attachmentToDelete?.filename}</span>? This action cannot be undone.
            </p>
          </DialogHeader>
          <div className="px-6 pb-6 flex items-center justify-end gap-3">
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              onClick={() => setShowDeleteAttachment(false)}
              disabled={deletingAttachment}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={confirmDeleteAttachment}
              disabled={deletingAttachment}
            >
              {deletingAttachment ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Experience Dialog */}
      <Dialog open={showExperienceDialog} onOpenChange={(open) => { if (!open) resetExperienceForm(); setShowExperienceDialog(open); }}>
        <DialogContent className="sm:max-w-[520px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">
              {editingExperienceId ? "Edit Experience" : "Add Experience"}
            </DialogTitle>
            <p className="text-sm text-gray-500 font-medium">
              {editingExperienceId ? "Update work experience details" : `Add a past role for ${candidate.firstName} ${candidate.lastName}`}
            </p>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Role Title <span className="text-red-500">*</span></label>
              <Input
                placeholder="e.g. Senior Software Engineer"
                value={expRoleTitle}
                onChange={(e) => setExpRoleTitle(e.target.value)}
                className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Company Name</label>
              <Input
                placeholder="e.g. Acme Corp"
                value={expCompanyName}
                onChange={(e) => setExpCompanyName(e.target.value)}
                className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Start Date</label>
                <Input
                  type="date"
                  value={expStartDate}
                  onChange={(e) => setExpStartDate(e.target.value)}
                  className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">End Date</label>
                <Input
                  type="date"
                  value={expEndDate}
                  onChange={(e) => setExpEndDate(e.target.value)}
                  className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Responsibilities</label>
              <Textarea
                placeholder="Describe key responsibilities and achievements..."
                value={expResponsibilities}
                onChange={(e) => setExpResponsibilities(e.target.value)}
                className="bg-gray-50 border-gray-100 rounded-xl text-sm font-medium min-h-[100px] resize-y focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button variant="outline" className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5" onClick={() => { resetExperienceForm(); setShowExperienceDialog(false); }}>
                Cancel
              </Button>
              <Button className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5" onClick={handleSaveExperience} disabled={savingExperience}>
                {savingExperience ? "Saving..." : (editingExperienceId ? "Update Experience" : "Add Experience")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Experience Dialog */}
      <Dialog open={showDeleteExperience} onOpenChange={setShowDeleteExperience}>
        <DialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">Delete Experience</DialogTitle>
            <p className="text-sm text-gray-500 font-medium">
              Are you sure you want to delete <span className="font-bold text-gray-700">{experienceToDelete?.roleTitle}</span>? This action cannot be undone.
            </p>
          </DialogHeader>
          <div className="px-6 pb-6 flex items-center justify-end gap-3">
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              onClick={() => setShowDeleteExperience(false)}
              disabled={deletingExperience}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={confirmDeleteExperience}
              disabled={deletingExperience}
            >
              {deletingExperience ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default CandidateDetail;
